import { setHours, setMinutes } from "date-fns";
import { useEffect, useState } from "react";
import Select, { ClassNamesConfig, GroupBase } from "react-select";

import { createClassNames } from "./react-select";

type TimeDropdownProps = {
  value: Date | null;
  minDate?: Date;
  onChange: (value: Date) => void;
  disabled?: boolean;
};

type Option = {
  label: string;
  value: number;
};

export function TimeDropdown({
  value,
  minDate,
  onChange,
  disabled,
}: TimeDropdownProps) {
  const [minute, setMinute] = useState<Option | null>(
    value
      ? {
          label: `${value.getMinutes()}`.padStart(2, "0"),
          value: value.getMinutes(),
        }
      : null,
  );
  const [hour, setHour] = useState<Option | null>(
    value
      ? {
          label: `${value.getHours()}`.padStart(2, "0"),
          value: value.getHours(),
        }
      : null,
  );

  const minutes: Option[] = Array.from({ length: 60 }, (_, i) => ({
    label: `${i}`.padStart(2, "0"),
    value: i,
  }));

  const hours: Option[] = Array.from({ length: 24 }, (_, i) => ({
    label: `${i}`.padStart(2, "0"),
    value: i,
  }));

  const minimumMinute = minDate ? new Date(minDate).getMinutes() : 0;
  const minimumHour = minDate ? new Date(minDate).getHours() : 0;
  const filteredMinutes = minutes.filter((min) =>
    hour?.value && hour.value > minimumHour ? true : min.value >= minimumMinute,
  );
  const filteredHours = hours.filter((hour) => hour.value >= minimumHour);

  const reactSelectClassNames = createClassNames({
    container: () => "w-14",
    control: () => "text-foreground",
  });

  useEffect(() => {
    if (!hour || !minute) return;
    const newDate = setMinutes(setHours(new Date(), hour.value), minute.value);
    onChange(newDate);
  }, [hour, minute, onChange]);

  return (
    <div className="flex flex-row gap-2">
      <Select<{ label: string; value: number }>
        unstyled
        components={{
          DropdownIndicator: () => null,
          ClearIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        classNames={
          reactSelectClassNames as ClassNamesConfig<
            { label: string; value: number },
            false,
            GroupBase<{ label: string; value: number }>
          >
        }
        isDisabled={disabled}
        placeholder="HH"
        options={filteredHours}
        getOptionLabel={(option) => option.label}
        value={hour}
        onChange={(option) => option && setHour(option)}
        noOptionsMessage={() => "-"}
      />

      <Select<{ label: string; value: number }>
        unstyled
        components={{
          DropdownIndicator: () => null,
          ClearIndicator: () => null,
          IndicatorSeparator: () => null,
        }}
        classNames={
          reactSelectClassNames as ClassNamesConfig<
            { label: string; value: number },
            false,
            GroupBase<{ label: string; value: number }>
          >
        }
        isDisabled={disabled}
        placeholder="MM"
        options={filteredMinutes}
        getOptionLabel={(option) => option.label}
        value={minute}
        onChange={(option) => option && setMinute(option)}
        noOptionsMessage={() => "-"}
      />
    </div>
  );
}
